import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Mail, Loader2, CheckCircle } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { newsletterSchema, validateForm } from '@/lib/validation';

interface NewsletterSignupProps {
  variant?: 'default' | 'compact' | 'inline';
  title?: string;
  description?: string;
  className?: string;
}

export const NewsletterSignup = ({
  variant = 'default',
  title = 'Stay Updated',
  description = 'Get the latest warehouse and automation insights delivered to your inbox.',
  className = '',
}: NewsletterSignupProps) => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { toast } = useToast();
  const location = useLocation();

  // Get UTM parameters
  const getUTMParams = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    
    if (!email) return;

    // Validate input
    const utmParams = getUTMParams();
    const formData = {
      email: email,
      first_name: firstName || undefined,
      source_page: location.pathname,
      utm_source: utmParams.utm_source,
      utm_medium: utmParams.utm_medium,
      utm_campaign: utmParams.utm_campaign,
    };

    const validation = validateForm(newsletterSchema, formData);
    
    if (validation.success === false) {
      setErrors(validation.errors);
      const errorMessages = Object.values(validation.errors);
      toast({
        title: "Validation Error",
        description: errorMessages.length > 0 ? String(errorMessages[0]) : "Please check your input",
        variant: "destructive",
      });
      return;
    }

    const validatedData = validation.data;
    setIsLoading(true);

    try {
      const { error } = await supabase.from('newsletter_subscriptions').insert({
        email: validatedData.email,
        first_name: validatedData.first_name,
        source_page: validatedData.source_page,
        utm_source: validatedData.utm_source,
        utm_medium: validatedData.utm_medium,
        utm_campaign: validatedData.utm_campaign,
      });

      if (error) {
        if (error.code === '23505') {
          toast({
            title: "Already subscribed!",
            description: "This email is already on our mailing list.",
          });
        } else {
          throw error;
        }
      } else {
        setIsSuccess(true);
        setEmail('');
        setFirstName('');
        toast({
          title: "Successfully subscribed!",
          description: "Thank you for joining our newsletter.",
        });
      }
    } catch (error) {
      console.error('Newsletter signup error:', error);
      toast({
        title: "Something went wrong",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className={`flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/20 rounded-lg ${className}`}>
        <CheckCircle className="w-5 h-5 text-green-500 shrink-0" />
        <p className="text-sm text-green-700 dark:text-green-300">
          Thanks for subscribing! Check your inbox for updates.
        </p>
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <form onSubmit={handleSubmit} className={`flex gap-2 ${className}`}>
        <Input
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="flex-1"
        />
        <Button type="submit" disabled={isLoading} size="sm">
          {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Subscribe'}
        </Button>
      </form>
    );
  }

  if (variant === 'compact') {
    return (
      <div className={`space-y-3 ${className}`}>
        <p className="text-sm font-medium">{title}</p>
        <form onSubmit={handleSubmit} className="flex gap-2">
          <Input
            type="email"
            placeholder="Your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="flex-1"
          />
          <Button type="submit" disabled={isLoading} size="icon">
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className={`p-6 bg-muted/50 rounded-xl border border-border ${className}`}>
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-primary/10 rounded-lg">
          <Mail className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h3 className="font-semibold text-foreground">{title}</h3>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input
            type="text"
            placeholder="First name (optional)"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          <Input
            type="email"
            placeholder="Email address *"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <Button type="submit" disabled={isLoading} className="w-full sm:w-auto">
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Subscribing...
            </>
          ) : (
            'Subscribe to Newsletter'
          )}
        </Button>
        <p className="text-xs text-muted-foreground">
          We respect your privacy. Unsubscribe at any time.
        </p>
      </form>
    </div>
  );
};
